import React, { useState, useEffect, useCallback } from 'react';
import styles from './QRCodePopup.module.css';

interface UserQRCodePopupProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  username?: string;
  name?: string;
}

const UserQRCodePopup: React.FC<UserQRCodePopupProps> = ({
  isOpen,
  onClose,
  userId,
  username,
  name,
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const generateQRCode = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { QRCodeService } = await import('../../services/qrCode.service');
      const qrService = QRCodeService.getInstance();

      // Generate QR code with user ID
      const dataUrl = await qrService.generateSimpleQRCode(userId);
      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      generateQRCode();
    }
  }, [isOpen, userId, generateQRCode]);

  const handleDownload = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.href = qrCodeDataUrl;
      link.download = `user-qr-${userId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(userId);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div
        className={styles.modalContainer}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>

        <div className={styles.modalContent}>
          <h2 className={styles.modalTitle}>User QR Code</h2>

          <div className={styles.affiliateInfo}>
            <div className={styles.infoRow}>
              <span className={styles.label}>User ID:</span>
              <span className={styles.value}>{userId}</span>
              <button
                className={styles.copyBtn}
                onClick={handleCopyToClipboard}
                title="Copy to clipboard"
              >
                📋
              </button>
            </div>
            {username && (
              <div className={styles.infoRow}>
                <span className={styles.label}>Username:</span>
                <span className={styles.value}>{username}</span>
              </div>
            )}
            {name && (
              <div className={styles.infoRow}>
                <span className={styles.label}>Name:</span>
                <span className={styles.value}>{name}</span>
              </div>
            )}
          </div>

          <div className={styles.qrContainer}>
            {loading ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>Generating QR Code...</p>
              </div>
            ) : error ? (
              <div className={styles.error}>
                <p>{error}</p>
                <button className={styles.retryBtn} onClick={generateQRCode}>
                  Retry
                </button>
              </div>
            ) : (
              <>
                <img
                  src={qrCodeDataUrl}
                  alt={`QR Code for User ID: ${userId}`}
                  className={styles.qrCode}
                />
                <p className={styles.qrDescription}>
                  Scan this QR code to get the user information
                </p>
              </>
            )}
          </div>

          <div className={styles.actionButtons}>
            <button
              className={styles.downloadBtn}
              onClick={handleDownload}
              disabled={!qrCodeDataUrl || loading}
            >
              Download QR Code
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserQRCodePopup;
